import { useEffect, useRef, useState } from "react";

/**
 * Lightweight touch+mouse rectangular cropper.
 *
 * Props:
 *   src        — image URL/dataURL/objectURL
 *   onConfirm  — async (blob, dataUrl) => void   (called when user presses Done)
 *   onCancel   — () => void                       (called when user retakes)
 *   confirmLabel / cancelLabel — optional button labels
 */
export default function CropOverlay({
  src,
  onConfirm,
  onCancel,
  confirmLabel = "Use cropped area",
  cancelLabel = "Retake",
}) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [box, setBox] = useState({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 }); // fractions of the rendered image
  const [drag, setDrag] = useState(null);
  const [busy, setBusy] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

  const onImageLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
  };

  const pointer = (e) => {
    const t = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: t.clientX, y: t.clientY };
  };

  const beginDrag = (mode, e) => {
    e.preventDefault();
    e.stopPropagation();
    const c = containerRef.current?.getBoundingClientRect();
    if (!c) return;
    const p = pointer(e);
    setDrag({ mode, startX: p.x, startY: p.y, startBox: { ...box }, rect: c });
  };

  useEffect(() => {
    if (!drag) return;
    const move = (e) => {
      const p = pointer(e);
      const dx = (p.x - drag.startX) / drag.rect.width;
      const dy = (p.y - drag.startY) / drag.rect.height;
      setBox((prev) => clampBox(applyDrag(drag.startBox, drag.mode, dx, dy), prev));
    };
    const end = () => setDrag(null);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  const handleConfirm = async () => {
    if (busy) return;
    if (!naturalSize.w || !naturalSize.h) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(src, box, naturalSize);
      const dataUrl = URL.createObjectURL(blob);
      await onConfirm?.(blob, dataUrl);
    } finally {
      setBusy(false);
    }
  };

  const handle = (mode, style) => (
    <div
      data-testid={`crop-handle-${mode}`}
      onMouseDown={(e) => beginDrag(mode, e)}
      onTouchStart={(e) => beginDrag(mode, e)}
      className="absolute w-7 h-7 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white border-4 border-pink-500 shadow-[0_3px_0_rgba(0,0,0,0.15)] cursor-grab active:cursor-grabbing touch-none"
      style={style}
    />
  );

  return (
    <div className="w-full">
      <div
        ref={containerRef}
        className="relative w-full aspect-[3/4] bg-slate-900 rounded-3xl overflow-hidden border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.12)] select-none touch-none"
      >
        <img
          ref={imgRef}
          src={src}
          alt="to crop"
          onLoad={onImageLoad}
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          draggable={false}
        />
        {/* dimmed overlay outside the crop area, using clip-path */}
        <div
          aria-hidden
          className="absolute inset-0 bg-black/55 pointer-events-none"
          style={{
            clipPath: `polygon(
              0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
              ${box.x * 100}% ${box.y * 100}%,
              ${box.x * 100}% ${(box.y + box.h) * 100}%,
              ${(box.x + box.w) * 100}% ${(box.y + box.h) * 100}%,
              ${(box.x + box.w) * 100}% ${box.y * 100}%,
              ${box.x * 100}% ${box.y * 100}%
            )`,
          }}
        />
        {/* draggable crop box */}
        <div
          data-testid="crop-box"
          onMouseDown={(e) => beginDrag("move", e)}
          onTouchStart={(e) => beginDrag("move", e)}
          className="absolute border-2 border-pink-400 ring-2 ring-white/80 cursor-move touch-none"
          style={{
            left: `${box.x * 100}%`,
            top: `${box.y * 100}%`,
            width: `${box.w * 100}%`,
            height: `${box.h * 100}%`,
          }}
        >
          {/* grid lines */}
          <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="border border-white/25" />
            ))}
          </div>
        </div>
        {/* corner handles (positioned absolutely inside container) */}
        {handle("tl", { left: `${box.x * 100}%`, top: `${box.y * 100}%` })}
        {handle("tr", { left: `${(box.x + box.w) * 100}%`, top: `${box.y * 100}%` })}
        {handle("bl", { left: `${box.x * 100}%`, top: `${(box.y + box.h) * 100}%` })}
        {handle("br", { left: `${(box.x + box.w) * 100}%`, top: `${(box.y + box.h) * 100}%` })}
      </div>

      <div
        className="mt-3 text-center text-xs font-semibold text-white"
        style={{ textShadow: "0 1px 3px rgba(15,23,42,0.65)" }}
      >
        Drag the corners or the box to frame your subject.
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <button
          data-testid="crop-cancel-btn"
          onClick={onCancel}
          disabled={busy}
          className="bg-white border-2 border-slate-900/10 text-slate-700 font-heading font-bold uppercase py-4 rounded-full btn-squish disabled:opacity-60"
        >
          {cancelLabel}
        </button>
        <button
          data-testid="crop-confirm-btn"
          onClick={handleConfirm}
          disabled={busy}
          className="bg-emerald-400 hover:bg-emerald-500 text-white font-heading font-bold uppercase py-4 rounded-full btn-squish disabled:opacity-60"
        >
          {busy ? "Cropping..." : confirmLabel}
        </button>
      </div>
    </div>
  );
}

const MIN = 0.06; // 6% min crop size relative to image

function applyDrag(start, mode, dx, dy) {
  const out = { ...start };
  if (mode === "move") {
    out.x = start.x + dx;
    out.y = start.y + dy;
  }
  if (mode.includes("l")) {
    out.x = start.x + dx;
    out.w = start.w - dx;
  }
  if (mode.includes("r")) {
    out.w = start.w + dx;
  }
  if (mode.includes("t")) {
    out.y = start.y + dy;
    out.h = start.h - dy;
  }
  if (mode.includes("b")) {
    out.h = start.h + dy;
  }
  return out;
}

function clampBox(b, prev) {
  let { x, y, w, h } = b;
  if (w < MIN) {
    w = MIN;
    if (x !== prev.x) x = prev.x + prev.w - MIN;
  }
  if (h < MIN) {
    h = MIN;
    if (y !== prev.y) y = prev.y + prev.h - MIN;
  }
  x = Math.max(0, Math.min(1 - w, x));
  y = Math.max(0, Math.min(1 - h, y));
  w = Math.max(MIN, Math.min(1 - x, w));
  h = Math.max(MIN, Math.min(1 - y, h));
  return { x, y, w, h };
}

/**
 * Crops the image to the given fractional box and returns a JPEG Blob.
 * We honour `object-contain`: the crop frame is relative to the rendered area,
 * which equals the full natural image since the source is rendered with `object-contain`
 * in a fixed-aspect container. We crop the natural image directly using the
 * same fractional coordinates — this gives the user-visible region.
 */
async function cropToBlob(src, box, natural) {
  const img = await loadImage(src);
  // The visible region of the image inside an object-contain container of
  // aspect 3:4 is the "letterboxed" rect. We need to convert fractional box
  // coordinates (relative to the container) to natural-image pixel coords.
  // Compute letterbox.
  const containerAspect = 3 / 4;
  const imgAspect = natural.w / natural.h;
  let visX = 0, visY = 0, visW = 1, visH = 1; // fractional rect of the visible image inside container
  if (imgAspect > containerAspect) {
    // image wider than container → letterbox top/bottom
    const h = containerAspect / imgAspect; // fraction of container height
    visY = (1 - h) / 2;
    visH = h;
  } else {
    // image taller than container → letterbox left/right
    const w = imgAspect / containerAspect;
    visX = (1 - w) / 2;
    visW = w;
  }
  // Convert crop box (relative to container) → relative to visible image rect.
  const relX = Math.max(0, (box.x - visX) / visW);
  const relY = Math.max(0, (box.y - visY) / visH);
  const relW = Math.min(1 - relX, box.w / visW);
  const relH = Math.min(1 - relY, box.h / visH);

  const sx = Math.round(relX * natural.w);
  const sy = Math.round(relY * natural.h);
  const sw = Math.max(1, Math.round(relW * natural.w));
  const sh = Math.max(1, Math.round(relH * natural.h));

  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

  return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92));
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
}
